import React, { useMemo, useState } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import {
  PerformancePeriodData,
  PipelinePeriodData,
} from '../../services/recommendations_api'
import { getChartColor } from '../../utils/chartColors'
import { formatCurrency } from '../../utils/formatting'
import { Tabs } from '../FbUI/Tabs'

// Register Chart.js components (required before using Bar/Line charts)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
)

/** Chart color indices from chartColors.ts palette. */
const CHART_COLORS = {
  DEALS_OPENED: 0, // Dark Green
  DEALS_WON: 1, // Light Green
  DEALS_LOST: 6, // Red
  EMAILS_SENT: 7, // Cyan
  EMAILS_RECEIVED: 4, // Lime
  VALUE_OPENED: 3, // Blue
  VALUE_WON: 1, // Light Green
  WEIGHTED_VALUE: 5, // Purple
} as const

/** Shared chart plugin config for legend and tooltip base styling. */
const SHARED_CHART_PLUGINS = {
  legend: {
    position: 'bottom' as const,
    labels: {
      usePointStyle: true,
      boxWidth: 8,
      padding: 16,
      font: { size: 11 },
    },
  },
  tooltip: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    padding: 12,
    titleFont: { size: 12 },
    bodyFont: { size: 11 },
    position: 'nearest' as const,
    // Hide tooltip lines where value is 0 to reduce clutter
    filter: (tooltipItem: { raw: unknown }) =>
      (tooltipItem.raw as number) !== 0,
  },
  // Datalabels plugin is globally registered — configure it to be clean:
  // hide 0 values and use white text so labels are readable on dark bars
  datalabels: {
    display: ((context: any) =>
      ((context.dataset.data[context.dataIndex] as number) ?? 0) >
      0) as unknown as boolean,
    color: '#fff',
    font: { size: 10, weight: 'bold' as const },
    anchor: 'end' as const,
    align: 'start' as const,
    offset: 2,
  },
} as const

/** Max pixel width for a single bar to prevent overlap when many periods. */
const MAX_BAR_THICKNESS = 32

/** Available tabs for the performance chart. */
type ChartTab = 'deals' | 'emails' | 'value' | 'pipeline'

interface PerformanceChartProps {
  /** Array of period data to display, ordered newest first from API. */
  periods: PerformancePeriodData[]
  /** Pipeline period data for the Pipeline tab. */
  pipelinePeriods?: PipelinePeriodData[]
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Grouped bar chart showing performance metrics over time.
 *
 * Features:
 * - Tabs to switch between Deals, Emails, and Value views
 * - Grouped bar chart for each category
 * - Tooltips show relevant context (win rate for deals, etc.)
 *
 * Color scheme:
 * - Deals opened: Dark Green (primary metric)
 * - Deals won: Light Green (success indicator)
 * - Deals lost: Red (failure indicator)
 * - Emails sent: Cyan
 * - Value opened: Blue
 * - Value won: Light Green
 */
export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  periods,
  pipelinePeriods,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<ChartTab>('deals')
  const tabOptions = useMemo(
    () => [
      { key: 'deals' as ChartTab, title: 'Deals' },
      { key: 'emails' as ChartTab, title: 'Emails' },
      { key: 'value' as ChartTab, title: 'Value' },
      ...(pipelinePeriods && pipelinePeriods.length > 0
        ? [{ key: 'pipeline' as ChartTab, title: 'Pipeline' }]
        : []),
    ],
    [pipelinePeriods]
  )

  // Reverse to show oldest to newest (left to right)
  const sortedPeriods = useMemo(() => [...periods].reverse(), [periods])
  const labels = useMemo(
    () => sortedPeriods.map((p) => p.period_label),
    [sortedPeriods]
  )

  const dealsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Deals Opened',
          data: sortedPeriods.map((p) => p.deals_opened),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_OPENED),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Deals Won',
          data: sortedPeriods.map((p) => p.deals_won),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_WON),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Deals Lost',
          data: sortedPeriods.map((p) => p.deals_lost),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_LOST),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const emailsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Emails Sent',
          data: sortedPeriods.map((p) => p.emails_sent),
          backgroundColor: getChartColor(CHART_COLORS.EMAILS_SENT),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Emails Received',
          data: sortedPeriods.map((p) => p.emails_received),
          backgroundColor: getChartColor(CHART_COLORS.EMAILS_RECEIVED),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const valueChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Value Opened',
          data: sortedPeriods.map((p) => p.value_opened),
          backgroundColor: getChartColor(CHART_COLORS.VALUE_OPENED),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Value Won',
          data: sortedPeriods.map((p) => p.value_won),
          backgroundColor: getChartColor(CHART_COLORS.VALUE_WON),
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  // Pipeline chart data (Line chart with dual y-axes)
  const sortedPipelinePeriods = useMemo(
    () => (pipelinePeriods ? [...pipelinePeriods].reverse() : []),
    [pipelinePeriods]
  )
  const pipelineLabels = useMemo(
    () => sortedPipelinePeriods.map((p) => p.period_label),
    [sortedPipelinePeriods]
  )

  const pipelineChartData = useMemo(
    () => ({
      labels: pipelineLabels,
      datasets: [
        {
          label: 'Unweighted Value',
          data: sortedPipelinePeriods.map((p) => p.unweighted_value),
          borderColor: getChartColor(CHART_COLORS.VALUE_OPENED),
          backgroundColor: getChartColor(CHART_COLORS.VALUE_OPENED),
          yAxisID: 'y',
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Weighted Value',
          data: sortedPipelinePeriods.map((p) => p.weighted_value),
          borderColor: getChartColor(CHART_COLORS.WEIGHTED_VALUE),
          backgroundColor: getChartColor(CHART_COLORS.WEIGHTED_VALUE),
          yAxisID: 'y',
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Deal Count',
          data: sortedPipelinePeriods.map((p) => p.deal_count),
          borderColor: getChartColor(CHART_COLORS.DEALS_OPENED),
          backgroundColor: getChartColor(CHART_COLORS.DEALS_OPENED),
          yAxisID: 'y1',
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    }),
    [pipelineLabels, sortedPipelinePeriods]
  )

  const getBarChartData = () => {
    switch (activeTab) {
      case 'deals':
        return dealsChartData
      case 'emails':
        return emailsChartData
      case 'value':
        return valueChartData
      default:
        return dealsChartData
    }
  }

  const barOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        ...SHARED_CHART_PLUGINS,
        datalabels: {
          ...SHARED_CHART_PLUGINS.datalabels,
          ...(activeTab === 'value'
            ? {
                formatter: (value: number) =>
                  formatCurrency(value, { decimals: false }),
                font: { size: 9, weight: 'bold' as const },
              }
            : {}),
        },
        tooltip: {
          ...SHARED_CHART_PLUGINS.tooltip,
          callbacks:
            activeTab === 'deals'
              ? {
                  afterBody: (tooltipItems) => {
                    const periodIndex = tooltipItems[0]?.dataIndex
                    if (periodIndex !== undefined) {
                      const period = sortedPeriods[periodIndex]
                      if (period?.win_rate !== null) {
                        return `\nWin Rate: ${period.win_rate}%`
                      }
                    }
                    return ''
                  },
                }
              : activeTab === 'value'
                ? {
                    label: (context) => {
                      const value = context.raw as number
                      return `${context.dataset.label}: ${formatCurrency(value)}`
                    },
                  }
                : undefined,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: { size: 10 },
            autoSkip: true,
            maxRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text:
              activeTab === 'deals'
                ? 'Deals'
                : activeTab === 'emails'
                  ? 'Emails'
                  : 'Value ($)',
            font: { size: 10 },
          },
          ticks: {
            font: { size: 10 },
            precision: 0,
            callback:
              activeTab === 'value'
                ? (value) =>
                    formatCurrency(value as number, { decimals: false })
                : undefined,
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
    }),
    [activeTab, sortedPeriods]
  )

  const pipelineOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        ...SHARED_CHART_PLUGINS,
        datalabels: { display: false },
        tooltip: {
          ...SHARED_CHART_PLUGINS.tooltip,
          callbacks: {
            label: (context) => {
              const value = context.raw as number
              if (context.dataset.yAxisID === 'y1') {
                return `${context.dataset.label}: ${value}`
              }
              return `${context.dataset.label}: ${formatCurrency(value)}`
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 } },
        },
        y: {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Value ($)',
            font: { size: 10 },
          },
          ticks: {
            font: { size: 10 },
            callback: (value) =>
              formatCurrency(value as number, { decimals: false }),
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
        },
        y1: {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Deals',
            font: { size: 10 },
          },
          ticks: {
            font: { size: 10 },
            precision: 0,
          },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    []
  )

  if (
    periods.length === 0 &&
    (!pipelinePeriods || pipelinePeriods.length === 0)
  ) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-48 text-fg-secondary text-sm">
          No data available for the selected period
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Tab buttons */}
      <div className="mb-3">
        <Tabs<ChartTab>
          tabs={tabOptions}
          selectedTab={activeTab}
          setSelectedTab={setActiveTab}
          isSmall
          noGap
        />
      </div>

      {/* Chart */}
      <div className="h-56 relative">
        {activeTab === 'pipeline' ? (
          <Line data={pipelineChartData} options={pipelineOptions} />
        ) : (
          <Bar data={getBarChartData()} options={barOptions} />
        )}
      </div>
    </div>
  )
}
